import React from "react";
import "./FlightResultCard.css";

/**
 * Presentation-only flight result shell: grid layout + dual-currency price strip.
 * Does not compute pricing; parents pass numeric values ready for display formatting.
 *
 * @param {"public"|"agent"} mode
 * @param {number|null|undefined} priceIls — ILS amount (per pax), already business-final where applicable
 * @param {number|null|undefined} priceUsd — USD amount for display (per pax or FX companion)
 * @param {boolean} isPrimaryCurrencyIls — true: ₪ hero, $ secondary; false: $ hero, ₪ secondary
 * @param {React.ReactNode} detailsContent — itinerary / legs column
 * @param {React.ReactNode} [airlineContent] — optional airline strip (public, or agent 3-col when set)
 * @param {React.ReactNode} [actions] — CTA / footers below price
 * @param {React.ReactNode} [priceAccessory] — e.g. trace button beside hero amount (agent)
 * @param {boolean} [isCheapest]
 * @param {string|null} [prominenceLabel] — overrides default "הכי זול" badge when set
 * @param {string|null} [primaryPriceLabel] — pre-formatted hero amount (e.g. EUR native)
 */
export default function FlightResultCard({
  mode = "public",
  appearance = "v1",
  priceIls = null,
  priceUsd = null,
  primaryPriceLabel = null,
  isPrimaryCurrencyIls = true,
  detailsContent,
  airlineContent = null,
  extrasContent = null,
  headerBadges = null,
  actions = null,
  priceAccessory = null,
  isCheapest = false,
  prominenceLabel = null,
  selected = false,
  className = "",
  totalLabel = null,
  /** When set, overrides default "סה״כ" under total (e.g. "סה״כ ל-2 נוסעים") */
  totalCaption = null,
}) {
  const formatIlsInt = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return null;
    return new Intl.NumberFormat("he-IL", { maximumFractionDigits: 0 }).format(
      Math.round(v)
    );
  };
  const formatUsdInt = (n) => {
    const v = Number(n);
    if (!Number.isFinite(v)) return null;
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
      Math.round(v)
    );
  };

  const ilsTxt = priceIls != null ? formatIlsInt(priceIls) : null;
  const usdTxt = priceUsd != null ? formatUsdInt(priceUsd) : null;

  const primaryDisplay =
    primaryPriceLabel != null && String(primaryPriceLabel).trim() !== ""
      ? String(primaryPriceLabel).trim()
      : isPrimaryCurrencyIls
        ? ilsTxt != null
          ? `₪${ilsTxt}`
          : null
        : usdTxt != null
          ? `$${usdTxt}`
          : null;

  const secondaryDisplay = isPrimaryCurrencyIls
    ? usdTxt != null
      ? `$${usdTxt}`
      : null
    : ilsTxt != null
      ? `₪${ilsTxt}`
      : null;

  const missingPrimary = primaryDisplay == null;

  const badgeText =
    prominenceLabel != null && String(prominenceLabel).trim() !== ""
      ? prominenceLabel
      : isCheapest
        ? "הכי זול"
        : null;

  const priceAside = (
    <aside className="flight-result-card__price" aria-label="מחיר" key="price">
      {badgeText ? (
        <span className="flight-result-card__badge">{badgeText}</span>
      ) : null}
      <div className="flight-result-card__price-hero">
        <div className="flight-result-card__price-hero-main">
          <strong
            className="flight-result-card__amount"
            translate="no"
            dir={appearance === "v2" ? "ltr" : undefined}
          >
            {missingPrimary
              ? "price unavailable"
              : appearance === "v2" && usdTxt
                ? `$${usdTxt}`
                : primaryDisplay}
          </strong>
          {appearance === "v2" ? (
            <span className="flight-result-card__price-caption">לנוסע</span>
          ) : null}
          {priceAccessory ? (
            <span className="flight-result-card__price-accessory">{priceAccessory}</span>
          ) : null}
        </div>
      </div>
      {appearance === "v2" ? null : (
        <span className="flight-result-card__price-caption">מחיר לנוסע</span>
      )}
      {appearance === "v2" && totalLabel ? (
        <span className="flight-result-card__total-line">
          <span className="flight-result-card__total" translate="no" dir="ltr">
            {totalLabel}
          </span>
          <span className="flight-result-card__price-caption">
            {totalCaption || "סה״כ"}
          </span>
        </span>
      ) : null}
      {appearance !== "v2" && secondaryDisplay != null && !missingPrimary && !primaryPriceLabel ? (
        <span
          className={`flight-result-card__secondary${
            isPrimaryCurrencyIls ? " flight-result-card__secondary--with-approx" : ""
          }`}
          translate="no"
        >
          {isPrimaryCurrencyIls ? "≈ " : ""}
          {secondaryDisplay}
        </span>
      ) : null}
      {actions ? <div className="flight-result-card__actions-slot">{actions}</div> : null}
    </aside>
  );

  const details = (
    <section className="flight-result-card__details" aria-label="פרטי טיסה" key="details">
      {detailsContent}
    </section>
  );

  const extras =
    appearance === "v2" && extrasContent != null ? (
      <aside className="flight-result-card__extras" aria-label="כבודה ומחלקה" key="extras">
        {extrasContent}
      </aside>
    ) : null;

  const airline =
    airlineContent != null && (mode === "public" || mode === "agent") ? (
      <aside className="flight-result-card__airline" aria-label="חברת תעופה" key="airline">
        {airlineContent}
      </aside>
    ) : null;

  const rootClass = [
    "flight-result-card",
    `flight-result-card--${mode === "agent" ? "agent" : "public"}`,
    appearance === "v2" ? "flight-result-card--v2" : "",
    appearance === "v2" && extrasContent != null ? "flight-result-card--v2-stack" : "",
    airlineContent ? "flight-result-card--with-airline" : "",
    selected ? "flight-result-card--selected" : "",
    "fw-ui-result-row",
    "fw-ui-result-row--rich-card",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (mode === "agent") {
    const agentThreeCol = Boolean(airlineContent);
    return (
      <div className={rootClass} dir="rtl">
        {appearance === "v2" && agentThreeCol ? (
          <>
            <header className="flight-result-card__head">
              {airline}
              {headerBadges}
            </header>
            {details}
            <footer className="flight-result-card__foot">
              {extras}
              {priceAside}
            </footer>
          </>
        ) : agentThreeCol ? (
          <>
            {priceAside}
            {details}
            {airline}
          </>
        ) : (
          <>
            {details}
            {priceAside}
          </>
        )}
      </div>
    );
  }

  return (
    <div className={rootClass} dir="rtl">
      {priceAside}
      {details}
      {airline}
    </div>
  );
}
